import * as React from "react";
import { useTranslation } from "react-i18next";
import { DashboardController } from "../../../controllers";
import { FinancialYear } from "../../../types";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import ResourceTable from "../../Resource/ResourceTable";
import ResourceTableRow from "../../Resource/ResourceTableRow";
import ResourceTableRowData from "../../Resource/ResourceTableRowData";
import styled from "styled-components";
import TableWidgetWrapper from "./TableWidgetWrapper";
import NumberFormatter from "../../../utilities/NumberFormatter";
import ResourceCreatablePowerSelect from "../../Form/ResourceCreatablePowerSelect";

const Container = styled.div`
  height: 100%;
`

interface FinancialYearSummaryRow {
  income: number;
  expenditure: number;
  cash_flow: number;
}

interface FinancialYearSummaryResponse {
  financial_year: FinancialYear;
  total_excluding: FinancialYearSummaryRow;
  total_vat: FinancialYearSummaryRow;
  total_invoiced: FinancialYearSummaryRow;
  total_outstanding: FinancialYearSummaryRow;
}

interface FinancialYearDataRow {
  label: string;
  income: number;
  expenditure: number;
  cash_flow: number;
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const ROW_KEYS: (keyof Pick<FinancialYearSummaryResponse, 'total_excluding' | 'total_vat' | 'total_invoiced' | 'total_outstanding'>)[] = [
  'total_excluding',
  'total_vat',
  'total_invoiced',
  'total_outstanding',
];

const FinancialYearWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [financialYear, setFinancialYear] = React.useState<FinancialYear>(null);
  const [data, setData] = React.useState<FinancialYearDataRow[]>([]);

  const LABEL_MAP: Record<keyof Pick<FinancialYearSummaryResponse, 'total_excluding' | 'total_vat' | 'total_invoiced' | 'total_outstanding'>, string> = {
    // i18n.t('FinancialYearWidget::Total excluding')
    total_excluding: t('FinancialYearWidget::Total excluding'),
    // i18n.t('FinancialYearWidget::Total VAT')
    total_vat: t('FinancialYearWidget::Total VAT'),
    // i18n.t('FinancialYearWidget::Total invoiced')
    total_invoiced: t('FinancialYearWidget::Total invoiced'),
    // i18n.t('FinancialYearWidget::Total outstanding')
    total_outstanding: t('FinancialYearWidget::Total outstanding'),
  };

  React.useEffect(() => {
    fetchData();
  }, [financialYear?.id]);

  const fetchData = async () => {
    try {
      const response: FinancialYearSummaryResponse = await DashboardController.getFinancialYearSummary(
        financialYear?.id
      );

      const rows: FinancialYearDataRow[] = ROW_KEYS.map((key) => ({
        label: LABEL_MAP[key],
        ...response[key],
      }));

      setData(rows);
      setFinancialYear(response.financial_year);
    } catch (error) {
      console.error("Failed to fetch financial year summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinancialYearChange = (id: string, financialYear: FinancialYear) => {
    setFinancialYear(financialYear);
  };

  if (isLoading) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={t('FinancialYearWidget::Financial year')}>
        <PageLoader />
      </Panel>
    );
  }

  return (
    <Container>
      <Panel
        className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto"
        title={t('FinancialYearWidget::Financial year')}
        headerRight={
          <div style={{ minWidth: '150px', fontWeight: 'normal' }}>
            <ResourceCreatablePowerSelect
              type='financial_year'
              placeholder={t('FinancialYearWidget::Year')}
              value={financialYear.id}
              onChange={onFinancialYearChange}
              isValidNewOption={() => false}
            />
          </div>
        }
      >
        <TableWidgetWrapper>
          <ResourceTable
            data={data}
            headers={[
              { title: t('FinancialYearWidget::Revenue') },
              { title: t('FinancialYearWidget::Income') },
              { title: t('FinancialYearWidget::Expenditure') },
              { title: t('FinancialYearWidget::Cash flow') }
            ]}
            renderRow={(item: FinancialYearDataRow) => {
              return (
                <ResourceTableRow>
                  <ResourceTableRowData>
                    {item.label}
                  </ResourceTableRowData>
                  <ResourceTableRowData>
                    {NumberFormatter.formatCurrency(currency, numberFormat, item.income)}
                  </ResourceTableRowData>
                  <ResourceTableRowData>
                    {NumberFormatter.formatCurrency(currency, numberFormat, item.expenditure)}
                  </ResourceTableRowData>
                  <ResourceTableRowData>
                    {NumberFormatter.formatCurrency(currency, numberFormat, item.cash_flow)}
                  </ResourceTableRowData>
                </ResourceTableRow>
              )
            }}
          />
        </TableWidgetWrapper>
      </Panel>
    </Container>
  )
}

export default FinancialYearWidget
